import type { EncounterModel } from '@ctw/shared/api/fhir/models/encounter';
import { ContentCard } from '@ctw/shared/components/containers/content-card';
import { DefinitionList } from '@ctw/shared/components/definition-list';
import { ResponsiveSourceDocumentLink } from '@ctw/shared/content/CCDA/responsive-source-document-link';
import { RelatedDocumentsList } from '@ctw/shared/content/related-documents/related-documents-list';
import type { UseResourceDrawerOpenDrawerArgs } from '@ctw/shared/context/resource-drawer-context';
import { tw } from '@ctw/shared/utils/tailwind';
import { capitalize } from 'lodash-es';

export const encounterDrawerAction: UseResourceDrawerOpenDrawerArgs<'Encounter'>['action'] = ({
  model,
}) => {
  const upid = model.patientUPID;
  const shouldShowSourceDocumentLink =
    upid !== undefined && upid !== '' && window.location.pathname.includes(upid);

  return {
    label: 'Encounter',
    title: model.title,
    width: 'wide',
    content: {
      body: (
        <div className={tw`flex flex-col gap-5`}>
          <ContentCard
            variant="focal"
            title="Details"
            content={{
              header: shouldShowSourceDocumentLink && (
                <ResponsiveSourceDocumentLink model={model} />
              ),
            }}
          >
            <DefinitionList
              items={[
                { label: 'Status', value: capitalize(model.status) },
                { label: 'Class', value: capitalize(model.classDisplay) },
                { label: 'Type', value: capitalize(model.typeDisplay) },
                { label: 'Location', value: model.location.join(', ') },
                {
                  label: 'Providers',
                  value: model.practitioners.length > 0 && (
                    <ul className={tw`m-0 list-disc pl-4`}>
                      {model.practitioners.map((p) => (
                        <li key={p}>{p}</li>
                      ))}
                    </ul>
                  ),
                },
                {
                  label: 'Location Type',
                  value: (() => {
                    const locationType = model.locationType.filter(
                      (value) => value.toLowerCase() !== 'unknown',
                    );
                    if (locationType.length > 0) {
                      return (
                        <ul className={tw`m-0 list-disc pl-4`}>
                          {locationType
                            .filter((value) => value.toLowerCase() !== 'unknown')
                            .map((s) => (
                              <li key={s}>{s}</li>
                            ))}
                        </ul>
                      );
                    }
                    return null;
                  })(),
                },
                { label: 'Reason', value: model.reason },
                {
                  label: 'Diagnoses',
                  value: model.diagnoses.length > 0 && (
                    <ul className={tw`m-0 list-disc pl-4`}>
                      {model.diagnoses.map((d) => (
                        <li key={d}>{d}</li>
                      ))}
                    </ul>
                  ),
                },
                { label: 'Discharge Disposition', value: model.dischargeDisposition },
              ]}
            />
          </ContentCard>
          <EncounterDocumentsSection model={model} />
        </div>
      ),
    },
  };
};

interface EncounterDocumentsSectionProps {
  model: EncounterModel;
}

const EncounterDocumentsSection = ({ model }: EncounterDocumentsSectionProps) => (
  <RelatedDocumentsList
    binaryIds={model.binaryIds}
    documentReferenceIds={model.adtDocRefIds}
    upid={model.patientUPID}
  />
);
